﻿import _ from 'lodash';

import Cookies from 'js-cookie';

import ServerError from '@/utils/server-error';

import defaults from './defaults';

/**
* OpenID Connect/OAuth 2.0 auth manager.
*
* Handles user authentication and token-based authorization by using OpenID Connect/OAuth 2.0 standards.
*/
export default class AuthManager {
    /**
     * Constructor
     *
     * Check if user session exists and initializes the authentication state.
     *
     * @return {void}
    */
    constructor(options) {
        this.options = _.merge(defaults, options);
        this.axios = options.axios;
        this.router = options.router;
        this.userStore = options.userStore;

        this._initNavigationGuards();
        this._initInterceptors();
    }

    /**
     * Login
     *
     * @param {string} username Username for logging in.
     * @param {string} password Password for logging in.
     * @param {boolean} rememberMe Remember user or not.
     * @param {string|null} redirect The route to redirect to.
     * @return {void}
     */
    async login(username, password, rememberMe, isGuest, redirect) {
        const data = {
            username: username,
            email: username,
            password: password,
            is_guest: isGuest,
            remember_me: rememberMe
        };

        await this._login(data, rememberMe, redirect);
    }

    /**
     * Updates user info
     *
     * @return {void}
     */
    async updateUserInfo(customerId) {
        try {
            let response = await this.axios.get(this.options.userInfoUri);
            let userInfo = !_.isEmpty(response.data) ? response.data : null;

            this.userStore.userInfo = null;

            if (userInfo !== null) {
                this.userStore.userInfo = {
                    id: customerId,
                    userName: response.data.username,
                    firstName: response.data.first_name,
                    lastName: response.data.last_name,
                    company: response.data.company
                };
            }
        }
        catch (error) {
            throw new ServerError(error);
        }
    }

    /**
     * Logout
     *
     * Clear all data in storage (which resets logged-in status) and redirect.
     *
     * @return {void}
     */
    async logout() {

        if (this.userStore.accessToken)
            await this.axios.get(this.options.logoutUri);

        this._endSession();
    }

    isRouteAccessible(route) {
        if (route.meta && route.meta.auth) {
            if (!this.userStore.isAuthenticated)
                return false;

            if (!this._tokenExists())
                return false;

            if (route.meta.auth.roles)
                return this.userStore.isInRole(route.meta.auth.roles);
        }

        return true;
    }

    /**
     * Internal login
     *
     * @param {object} data Data to send to the token endpoint.
     * @param {string|null} redirect The route to redirect to.
     * @return {void}
     */
    async _login(data, rememberMe, redirect) {
        try {
            let response = await this.axios.post(this.options.tokenUri, data);

            this._storeToken(response);

            //We use here custom 'userSessionMarker' and don`t use '.Nop.Authentication'
            //because it is HttpOnly and change it I think it`s really bad idea (But we can do it and it is works fine)

            if (rememberMe)
                Cookies.set('userSessionMarker', 'userSessionMarker', { expires: this.options.rememberMeDuration });
            else
                Cookies.set('userSessionMarker', 'userSessionMarker');

            await this.updateUserInfo(response.data.customer_id);

            if (redirect)
                this.router.push(redirect);
        }
        catch (error) {
            throw new ServerError(error);
        }
    }

    /**
    * Clears user's authentication data and redirect's to configured logoutRedirectTo route.
    *
    * @private
    * @return {void}
    */
    _endSession(redirectToLogoutRoute = true) {
        Cookies.remove('userSessionMarker');

        this.userStore.reset();

        if (redirectToLogoutRoute && this.router.currentRoute.value.name !== this.options.logoutRedirectTo)
            this.router.push({ name: this.options.logoutRedirectTo });
    }

    /**
    * Set the Authorization header on Axios Request.
    *
    * @private
    * @param {Object} config The Axios request's config to set the header on.
    * @return {void}
    */
    _setAuthHeader(config) {
        config.headers['Authorization'] = 'Bearer ' + this.userStore.accessToken;
    }

    /**
     * Store tokens
     *
     * Update the storage with the access/refresh tokens received from the token endpoint from the OpenID Connect server.
     *
     * @private
     * @param {Object} response Axios's response instance from the server that contains our tokens.
     * @return {void}
     */
    _storeToken(response) {
        this.userStore.$patch({
            accessToken: response.data.token,
        });
    }

    /**
     * Check if the Axios's response is an invalid token response.
     *
     * @private
     * @param {Object} response The Axios's response instance received from an http call.
     * @return {boolean}
     */
    _isInvalidToken(response) {
        if (!response)
            return false;

        if (!response.headers)
            return false;

        const status = response.status;
        const wwwAuthenticateHeader = response.headers['www-authenticate'];

        return (status === 401 && wwwAuthenticateHeader && (wwwAuthenticateHeader.includes('invalid_token') || wwwAuthenticateHeader.includes('expired_token')));
    }

    /**
     * Ensures data integrity.
     *
     * @private
     * @return {boolean}
     */
    _ensureDataIntegrity() {
        //if there is no token data then session marker should not exist
        if (!this.userStore.accessToken && Cookies.get('userSessionMarker'))
            return false;

        //if there is token data then session marker should exist as well
        if (this.userStore.accessToken && !Cookies.get('userSessionMarker'))
            return false;

        return true;
    }

    /**
     * Checks if token data exists.
     *
     * @private
     * @return {boolean}
     */
    _tokenExists() {
        return this._ensureDataIntegrity() && this.userStore.accessToken;
    }

    /**
    * Client-side authorization checks during navigation.
    *
    * @private
    * @return {void}
    */
    async _initNavigationGuards() {
        this.router.beforeEach((to, from) => {
            if (!this._ensureDataIntegrity()) {
                this._endSession(false);
                return { name: this.options.logoutRedirectTo };
            }

            if (!this.isRouteAccessible(to)) {
                if (!this.userStore.isAuthenticated)
                    return { name: this.options.unauthorizedRedirectTo, query: { redirect: to.path } };
                else
                    return { name: this.options.forbiddenRedirectTo };
            }
        });
    }

    /**
    * Add interceptors for adding authentication header, refreshing token and handling errors.
    *
    * @private
    * @return {void}
    */
    async _initInterceptors() {
        this.axios.interceptors.request.use(config => {

            if (this._tokenExists())
                this._setAuthHeader(config);

            return config;
        });

        this.axios.interceptors.response.use(response => {
            //For new version, simply reload on any get
            let version = response.headers['trimm-version'] || 'default';

            if (version !== Cookies.get('trimm-version')) {
                Cookies.set('trimm-version', version, { expires: this.options.rememberMeDuration });
                window.location.reload();
            }

            return response;
        },
            error => {
                if (this._isInvalidToken(error.response))
                    return this.logout();

                if (error.response) {
                    if (error.response.status === 401)
                        this.router.push({ name: this.options.unauthorizedRedirectTo, query: { redirect: this.router.currentRoute.value.path } });

                    if (error.response.status === 403)
                        this.router.replace({ name: this.options.forbiddenRedirectTo });
                }

                return Promise.reject(error);
            });
    }
}